import { Group, SegmentedControl } from "@mantine/core";
import { BarChart } from "@mantine/charts";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import useSWR from "swr";
import { ReturnAnalyticsApi } from "../../../apis";
import { dateFormat } from "../../../utils/date";
import { ListState } from "../../ui";
import { DetailCard } from "../detail-card";
import classes from "./trend-chart.module.css";

interface IProps {
  dateFromIso?: string;
  dateToIso?  : string;
}

/** Created / resolved / disputed per day or week (canvas Ord_ReturnDash). Slate, green, amber only. */
export function TrendChart({ dateFromIso, dateToIso }: IProps) {
  const { t } = useTranslation("returns");
  const [bucket, setBucket] = useState<"day" | "week">("day");

  const { data, error, mutate } = useSWR(
    ["returns.trend", dateFromIso, dateToIso, bucket],
    () => ReturnAnalyticsApi.trend({ dateFrom: dateFromIso, dateTo: dateToIso, bucket }),
    { revalidateOnFocus: false }
  );

  const series = (data?.points ?? []).map((p) => ({
    label   : dateFormat(p.bucketStart),
    created : p.count,
    resolved: p.resolved,
    disputed: p.disputed
  }));

  return (
    <DetailCard
      title={t("dashboard.trend.title")}
      extra={
        <SegmentedControl
          value={bucket}
          onChange={(v) => setBucket(v as "day" | "week")}
          data={[
            { value: "day",  label: t("dashboard.trend.day") },
            { value: "week", label: t("dashboard.trend.week") }
          ]}
          aria-label={t("dashboard.trend.bucketAria")}
        />
      }
    >
      {error && !data ? (
        <ListState state="error" onRetry={() => mutate()} />
      ) : (
        <Group mt="sm" grow>
          <BarChart
            className={classes.chart}
            h={220}
            // Canvas Ord_ReturnDash: slim bars, not full-width blocks.
            maxBarWidth={14}
            data={series}
            dataKey="label"
            series={[
              { name: "created",  color: "gray.7",   label: t("dashboard.trend.created") },
              { name: "resolved", color: "green.6",  label: t("dashboard.trend.resolved") },
              { name: "disputed", color: "yellow.6", label: t("dashboard.trend.disputed") }
            ]}
            withLegend
            legendProps={{ verticalAlign: "bottom" }}
            gridAxis="none"
            withYAxis={false}
            tickLine="none"
            valueFormatter={(v) => v.toLocaleString("mn-MN")}
          />
        </Group>
      )}
    </DetailCard>
  );
}
